import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtMoney, fmtNum, fmtDateTime, Badge } from "../lib/format";
import { StatCard } from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  Banknote, TrendingUp, Wallet, Package, AlertTriangle, XCircle, Truck, BadgePercent, HandCoins, Boxes, PiggyBank,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const cur = user?.currency || "FCFA";
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div data-testid="dashboard-loading" className="py-20 text-center text-muted-foreground">Chargement du tableau de bord...</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Tableau de Bord</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        <StatCard testid="stat-ca-today" label="CA Aujourd'hui" value={fmtMoney(d.ca_today, cur)} icon={Banknote} tone="blue" sub={`${d.sales_count_today} vente(s)`} />
        <StatCard testid="stat-ca-month" label="CA du mois" value={fmtMoney(d.ca_month, cur)} icon={TrendingUp} tone="emerald" sub={`${d.sales_count_month} vente(s)`} />
        {d.profit_month !== undefined && <StatCard testid="stat-profit-month" label="Marge brute (mois)" value={fmtMoney(d.profit_month, cur)} icon={PiggyBank} tone="amber" />}
        <StatCard testid="stat-purchases" label="Achats (mois)" value={fmtMoney(d.purchases_month, cur)} icon={Truck} tone="slate" />
        <StatCard testid="stat-expenses" label="Dépenses (mois)" value={fmtMoney(d.expenses_month, cur)} icon={Wallet} tone="rose" />
        <StatCard testid="stat-receivables" label="Créances clients" value={fmtMoney(d.receivables, cur)} icon={BadgePercent} tone="amber" />
        <StatCard testid="stat-debts" label="Dettes fournisseurs" value={fmtMoney(d.debts, cur)} icon={HandCoins} tone="rose" />
        <StatCard testid="stat-stock-value" label="Valeur du stock" value={fmtMoney(d.stock_value, cur)} icon={Boxes} tone="blue" sub={`${d.products_count} produits`} />
        <StatCard testid="stat-cash" label="Trésorerie totale" value={fmtMoney(d.cash_total, cur)} icon={Banknote} tone="emerald" />
        <StatCard testid="stat-low-stock" label="Stock faible" value={fmtNum(d.low_stock_count)} icon={AlertTriangle} tone="amber" />
        <StatCard testid="stat-out-stock" label="Ruptures" value={fmtNum(d.out_stock_count)} icon={XCircle} tone="rose" />
        <StatCard testid="stat-products" label="Produits actifs" value={fmtNum(d.products_count)} icon={Package} tone="slate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border rounded-xl p-4">
          <h2 className="font-heading font-bold mb-3">Ventes des 14 derniers jours</h2>
          <div className="h-64" data-testid="chart-sales-by-day">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.sales_by_day}>
                <defs>
                  <linearGradient id="ca" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => fmtMoney(v, cur)} />
                <Area type="monotone" dataKey="total" stroke="#2563EB" fill="url(#ca)" strokeWidth={2} name="CA" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <h2 className="font-heading font-bold mb-3">Meilleures ventes</h2>
          <div className="space-y-2" data-testid="top-products">
            {d.top_products.length === 0 && <p className="text-sm text-muted-foreground">Aucune vente pour le moment.</p>}
            {d.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate font-medium">{p.name}</span>
                <span className="font-money font-bold shrink-0">{fmtMoney(p.revenue, cur)}</span>
              </div>
            ))}
          </div>
          {(d.low_stock.length > 0 || d.out_stock.length > 0) && (
            <>
              <h2 className="font-heading font-bold mt-6 mb-2 text-amber-600 dark:text-amber-400">Alertes stock</h2>
              <div className="space-y-1.5" data-testid="stock-alerts">
                {d.out_stock.map((p) => (
                  <div key={p.id} className="text-xs px-2 py-1.5 rounded bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300 font-medium">Rupture : {p.name}</div>
                ))}
                {d.low_stock.map((p) => (
                  <div key={p.id} className="text-xs px-2 py-1.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300 font-medium">
                    Faible : {p.name} ({fmtNum(p.stock)} {p.unit})
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-card border rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold">Dernières ventes</h2>
            <Link to="/ventes" className="text-xs text-primary font-semibold hover:underline">Tout voir</Link>
          </div>
          <div className="space-y-2" data-testid="recent-sales">
            {d.recent_sales.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 text-sm border-b last:border-0 pb-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{s.number} — {s.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{fmtDateTime(s.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge status={s.payment_status} />
                  <span className="font-money font-bold">{fmtMoney(s.total, cur)}</span>
                </div>
              </div>
            ))}
            {d.recent_sales.length === 0 && <p className="text-sm text-muted-foreground">Aucune vente.</p>}
          </div>
        </div>
        <div className="bg-card border rounded-xl p-4">
          <h2 className="font-heading font-bold mb-3">Derniers mouvements de stock</h2>
          <div className="space-y-2" data-testid="recent-movements">
            {d.recent_movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2 text-sm border-b last:border-0 pb-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{m.product_name}</div>
                  <div className="text-xs text-muted-foreground">{m.reason || m.type} · {fmtDateTime(m.created_at)}</div>
                </div>
                <span className={`font-money font-bold shrink-0 ${m.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {m.delta >= 0 ? "+" : ""}{fmtNum(m.delta)}
                </span>
              </div>
            ))}
            {d.recent_movements.length === 0 && <p className="text-sm text-muted-foreground">Aucun mouvement.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
